import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { MapPin, Users, Award, Clock, Star } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const createIcon = (color) => new L.Icon({
  iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const ICONS = {
  active: createIcon('green'),
  approved: createIcon('blue'),
  trial_expired: createIcon('red'),
};

const FitBounds = ({ markers }) => {
  const map = useMap();
  useEffect(() => {
    if (markers.length > 0) {
      const bounds = markers.map(m => [m.lat, m.lng]);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 10 });
    }
  }, [markers, map]);
  return null;
};

const AdminMap = () => {
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMapData();
  }, []);

  const fetchMapData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/admin/engineer-map-data`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });
      setMarkers(res.data.markers || []);
    } catch (err) {
      console.error('Map data error:', err);
    } finally {
      setLoading(false);
    }
  };

  const statusLabel = (m) => {
    if (m.status === 'active' && m.trialActive) return 'Active (Trial)';
    if (m.status === 'active') return 'Active (Paid)';
    if (m.status === 'approved') return 'Approved';
    if (m.status === 'trial_expired') return 'Trial Expired';
    return m.status;
  };

  const activeCnt = markers.filter(m => m.status === 'active').length;
  const approvedCnt = markers.filter(m => m.status === 'approved').length;
  const expiredCnt = markers.filter(m => m.status === 'trial_expired').length;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b' }}>
        <div className="loading-spinner" />
        <p>Loading map data...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-map-section">
      <div className="admin-map-stats">
        <div className="admin-map-stat">
          <span className="admin-map-dot" style={{ background: '#22c55e' }} />
          Active: {activeCnt}
        </div>
        <div className="admin-map-stat">
          <span className="admin-map-dot" style={{ background: '#3b82f6' }} />
          Approved: {approvedCnt}
        </div>
        <div className="admin-map-stat">
          <span className="admin-map-dot" style={{ background: '#ef4444' }} />
          Expired: {expiredCnt}
        </div>
        <div className="admin-map-stat" style={{ marginLeft: 'auto', fontWeight: 600 }}>
          Total: {markers.length}
        </div>
      </div>

      <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
        <MapContainer
          center={[54.0, -2.5]}
          zoom={6}
          style={{ height: '550px', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds markers={markers} />

          {markers.map((m, idx) => (
            <Marker
              key={idx}
              position={[m.lat, m.lng]}
              icon={ICONS[m.status] || ICONS.active}
            >
              <Popup>
                <div style={{ minWidth: '180px' }}>
                  <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '4px' }}>{m.name}</div>
                  <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px' }}>{m.business}</div>
                  <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                    <strong>Status:</strong> {statusLabel(m)}
                  </div>
                  {m.postcode && (
                    <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                      <strong>Base:</strong> {m.postcode}
                    </div>
                  )}
                  {m.coverageAreas?.length > 0 && (
                    <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                      <strong>Coverage:</strong> {m.coverageAreas.join(', ')}
                    </div>
                  )}
                  {m.isFoundingMember && (
                    <div style={{ fontSize: '11px', color: '#ca8a04', fontWeight: 600, marginTop: '4px' }}>
                      Founding Member
                    </div>
                  )}
                  {m.reviewCount > 0 && (
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                      {m.rating} rating ({m.reviewCount} reviews)
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {markers.length === 0 && (
        <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>
          No engineers with geocodable postcodes found.
        </div>
      )}
    </div>
  );
};

export default AdminMap;
